import { FC } from 'react';
import { Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { KPIRow, KPICard, StyledStatistic } from '../EU6Calculator.styles';
import { KPIMetrics, Currency } from '../EU6Calculator.types';
import { getCurrencySymbol } from '@/constants/currency';
import { NAMESPACE } from '@/constants';

interface KPISummaryProps {
  metrics: KPIMetrics;
  currency: Currency;
  /** Show unallocated delivery hint under Total Gross Budget (e.g. package detail edit mode). */
  showDeliveryHint?: boolean;
}

const formatCurrency = (value: number, currency: Currency): string =>
  `${getCurrencySymbol(currency)}${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const formatPercentage = (value: number): string => `${value.toFixed(2)}%`;

const DELIVERY_SUM_EPSILON = 1e-6;

export const KPISummary: FC<KPISummaryProps> = ({ metrics, currency, showDeliveryHint = false }) => {
  const { t } = useTranslation(NAMESPACE.COMMON);

  const deliveryShortOf100 =
    metrics.totalDeliveryPercentage < 100 - DELIVERY_SUM_EPSILON;
  const impliedPlanTotal =
    metrics.totalDeliveryPercentage > DELIVERY_SUM_EPSILON
      ? (metrics.totalGrossBudgetPlan / metrics.totalDeliveryPercentage) * 100
      : 0;
  const unallocatedPlanGross = Math.max(0, impliedPlanTotal - metrics.totalGrossBudgetPlan);
  const grossBelowPlan =
    showDeliveryHint && deliveryShortOf100 && unallocatedPlanGross > 0.5;

  return (
  <>
    {/* Row 1: Sell CPM, Gross Budget, Net Budget */}
    <KPIRow gutter={[12, 12]}>
      <Col span={8}>
        <KPICard style={{ height: '80px' }}>
          <StyledStatistic
            title="Sell CPM"
            value={metrics.sellCPM}
            formatter={(value) => formatCurrency(value as number, currency)}
            valueStyle={{ fontSize: '16px' }}
          />
        </KPICard>
      </Col>

      <Col span={8}>
        <KPICard style={{ height: grossBelowPlan ? 'auto' : '80px', minHeight: '80px' }}>
          <StyledStatistic
            title="Total Gross Budget"
            value={metrics.totalGrossBudgetPlan}
            formatter={(value) => formatCurrency(value as number, currency)}
            valueStyle={{ fontSize: '16px' }}
          />
          {grossBelowPlan && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Rows allocate {formatPercentage(metrics.totalDeliveryPercentage)} of plan total
              {impliedPlanTotal > 0 && unallocatedPlanGross > 0.5
                ? ` (${formatCurrency(unallocatedPlanGross, currency)} unallocated)`
                : ''}
            </Typography.Text>
          )}
        </KPICard>
      </Col>

      <Col span={8}>
        <KPICard style={{ height: '80px' }}>
          <StyledStatistic
            title="Total Net Budget"
            value={metrics.totalNetBudgetPlan}
            formatter={(value) => formatCurrency(value as number, currency)}
            valueStyle={{ fontSize: '16px' }}
          />
        </KPICard>
      </Col>
    </KPIRow>

    {/* Row 2: Gross Margin, Gross Profit */}
    <KPIRow gutter={[16, 16]}>
      <Col span={12}>
        <KPICard>
          <StyledStatistic
            title={t('grossMarginBeforeRebate')}
            value={metrics.totalGrossMarginPlan}
            formatter={(value) => formatCurrency(value as number, currency)}
          />
        </KPICard>
      </Col>

      <Col span={12}>
        <KPICard>
          <StyledStatistic
            title={t('grossProfitAfterRebate')}
            value={metrics.totalGrossProfitPlan}
            formatter={(value) => formatCurrency(value as number, currency)}
          />
        </KPICard>
      </Col>
    </KPIRow>

    {/* Row 3: Margin %, Profit % */}
    <KPIRow gutter={[16, 16]}>
      <Col span={12}>
        <KPICard>
          <StyledStatistic
            title={t('blendedGrossMarginPercent')}
            value={metrics.blendedMarginPercentage}
            formatter={(value) => formatPercentage(value as number)}
          />
        </KPICard>
      </Col>

      <Col span={12}>
        <KPICard>
          <StyledStatistic
            title={t('blendedGrossProfitPercent')}
            value={metrics.blendedProfitPercentage}
            formatter={(value) => formatPercentage(value as number)}
          />
        </KPICard>
      </Col>
    </KPIRow>
  </>
  );
};
